import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { TaxAndFeeSetting } from "../../../types/admin";

interface TaxesFeesModalProps {
  open: boolean;
  mode: "create" | "edit";
  initialData?: TaxAndFeeSetting;
  onClose: () => void;
  onSave: (payload: {
    tax: number | null;
    fees: number | null;
    min: number;
    max: number;
    isActive: boolean;
    comment: string;
  }) => Promise<void> | void;
  submitting?: boolean;
}

const TaxesFeesModal: React.FC<TaxesFeesModalProps> = ({
  open,
  mode,
  initialData,
  onClose,
  onSave,
  submitting,
}) => {
  const { t } = useTranslation();
  const [tax, setTax] = useState<number | null>(initialData?.tax ?? null);
  const [fees, setFees] = useState<number | null>(initialData?.fees ?? null);
  const [min, setMin] = useState<number>(initialData?.min ?? 0);
  const [max, setMax] = useState<number>(initialData?.max ?? 0);
  const [isActive, setIsActive] = useState<boolean>(
    initialData?.isActive ?? true
  );
  const [comment, setComment] = useState<string>(initialData?.comment ?? "");

  useEffect(() => {
    if (open) {
      setTax(initialData?.tax ?? null);
      setFees(initialData?.fees ?? null);
      setMin(initialData?.min ?? 0);
      setMax(initialData?.max ?? 0);
      setIsActive(initialData?.isActive ?? true);
      setComment(initialData?.comment ?? "");
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    await onSave({
      tax: tax !== null && tax !== undefined ? Number(tax) : null,
      fees: fees !== null && fees !== undefined ? Number(fees) : null,
      min: Number(min) || 0,
      max: Number(max) || 0,
      isActive,
      comment: comment || "",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === "create" ? t("admin.createTaxesFees") : t("admin.editTaxesFees")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stack spacing={2}>
            <TextField
              label={t("admin.taxPercent")}
              type="number"
              value={tax ?? ""}
              onChange={(e) =>
                setTax(e.target.value === "" ? null : Number(e.target.value))
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              helperText={t("admin.taxHelperText")}
            />
            <TextField
              label={t("admin.feesPercent")}
              type="number"
              value={fees ?? ""}
              onChange={(e) =>
                setFees(e.target.value === "" ? null : Number(e.target.value))
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              helperText={t("admin.feesHelperText")}
            />
            <TextField
              label={t("admin.minAmount")}
              type="number"
              value={min}
              onChange={(e) => setMin(Number(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              required
              helperText={t("admin.minAmountHelperText")}
            />
            <TextField
              label={t("admin.maxAmount")}
              type="number"
              value={max}
              onChange={(e) => setMax(Number(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              required
              helperText={t("admin.maxAmountHelperText")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={t("common.active")}
            />
            <TextField
              label={t("common.comment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText={t("admin.taxFeeCommentHelperText")}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {mode === "create" ? t("common.create") : t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaxesFeesModal;
